import { useMemo } from "react"

import { Alert, Drawer, Flex, Tabs, Tag, Typography } from "antd"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import FileDownload from "js-file-download"
import { useTranslation } from "react-i18next"

import Button from "../../../../../Components/Button"
import Icon from "../../../../../Icon"
import { ensureHttps } from "../../../../../Utils/utils"
import { PROGRAM_META } from "../../constants"
import StatusTag, { RECEIPT_STATUS } from "../StatusTag/StatusTag"

import "./styles.less"

const { Text, Link, Paragraph } = Typography
dayjs.extend(utc)

const utcDate = (v) =>
  v ? `${dayjs.utc(v).format("DD MMM YYYY")} ${dayjs.utc(v).format("h:mm A [(UTC)]")}` : "-"

// antd Text types are theme-safe. Digital receipt status colors as plain text in the drawer:
// enrolled=green, pending=amber, rejected=red, unenrolled=grey, needs_update=blue.
const STATUS_TEXT_TYPE = {
  enrolled: "success",
  pending: "warning",
  rejected: "danger",
  unenrolled: "secondary"
}

const SectionHeader = ({ label }) => (
  <Flex align="center" gap={12} className="dr-section">
    <Text type="secondary" className="dr-section-label">
      {label}
    </Text>
    <div className="dr-section-line" />
  </Flex>
)

// A label + a copyable value (plain text, or a blue link when `link`).
const Field = ({ label, value, link, full, children }) => (
  <Flex vertical gap={4} className={full ? "dr-field dr-field-full" : "dr-field"}>
    <Text type="secondary" className="dr-field-label">
      {label}
    </Text>
    {children ? (
      children
    ) : value ? (
      link ? (
        <Link
          href={ensureHttps(value)}
          target="_blank"
          rel="noreferrer"
          copyable={{ text: value, tooltips: false }}
        >
          {value}
        </Link>
      ) : (
        <Text className="dr-field-value" copyable={{ text: value, tooltips: false }}>
          {value}
        </Text>
      )
    ) : (
      <Text className="dr-field-value">-</Text>
    )}
  </Flex>
)

const Badges = ({ values }) =>
  values?.length ? (
    <Flex gap={8} wrap="wrap">
      {values.map((v, i) => (
        <Tag key={`${v}-${i}`} bordered={false} className="dr-chip">
          <Text copyable={{ text: v, tooltips: false }}>{v}</Text>
        </Tag>
      ))}
    </Flex>
  ) : (
    <Text className="dr-field-value">-</Text>
  )

const OverviewTab = ({ record, t, onDownloadLogo }) => {
  const initial = (record.business_account || "?").trim().charAt(0).toUpperCase()
  const program = (record.programs || [])
    .map((p) => PROGRAM_META[p]?.label)
    .filter(Boolean)
    .join(", ")
  const status = record.request_status || record.status

  return (
    <Flex vertical gap={24} className="dr-overview">
      <div>
        <SectionHeader label={t("Business logo")} />
        <Flex align="center" justify="space-between" gap={16} className="dr-logo-row">
          <div className="dr-logo">
            {record.logo_url ? (
              <img src={record.logo_url} alt={record.business_account} />
            ) : (
              <span>{initial}</span>
            )}
          </div>
          <Button
            className="flex center small-space"
            icon={<Icon type="file_download" />}
            onClick={onDownloadLogo}
          >
            {t("Download logo")}
          </Button>
        </Flex>
      </div>

      <div>
        <SectionHeader label={t("Overview")} />
        <div className="dr-grid">
          <Field label={t("Request status")}>
            {status === "needs_update" ? (
              <Text className="blue dr-field-value">{t("Needs update")}</Text>
            ) : (
              <Text type={STATUS_TEXT_TYPE[status]} className="dr-field-value">
                {t(RECEIPT_STATUS[status]?.label || status)}
              </Text>
            )}
          </Field>
          <Field label={t("Business website")} value={record.business_website} link />
          <Field label={t("Requested on")} value={utcDate(record.created_timestamp)} />
          <Field
            label={t("Status updated on")}
            value={utcDate(record.status_updated_on || record.updated_timestamp)}
          />
          <Field label={t("Requester name")} value={record.requester_name} />
          <Field label={t("Requester email")} value={record.requester_email} />
          <Field label={t("Merchant ID")} value={record.merchant_id} />
          <Field label={t("Program")} value={program} />
        </div>
      </div>

      <div>
        <SectionHeader label={t("Business information")} />
        <div className="dr-grid">
          <Field label={t("Business name")} value={record.business_name} />
          <Field label={t("Business website")} value={record.business_website} link />
        </div>
      </div>

      <div>
        <SectionHeader label={t("Business details")} />
        <div className="dr-grid">
          <Field full label={t("Company address")} value={record.company_address} />
          <Field full label={t("Product / Service name")} value={record.product_name} />
          <Field full label={t("Product description")}>
            {record.product_description ? (
              <Paragraph
                className="dr-field-value"
                style={{ marginBottom: 0 }}
                copyable={{ text: record.product_description, tooltips: false }}
              >
                {record.product_description}
              </Paragraph>
            ) : (
              <Text className="dr-field-value">-</Text>
            )}
          </Field>
        </div>
      </div>

      <div>
        <SectionHeader label={t("Payment identifiers")} />
        <div className="dr-grid">
          <Field full label={t("Billing descriptors")}>
            <Badges values={record.billing_descriptors} />
          </Field>
          <Field full label={t("Acquirer BIN(s)")}>
            <Badges values={record.acquirer_bins} />
          </Field>
          <Field full label={t("Card Acceptor ID / CAID(s)")}>
            <Badges values={record.caids} />
          </Field>
        </div>
      </div>

      <div>
        <SectionHeader label={t("Support details")} />
        <div className="dr-grid">
          <Field label={t("Support phone")} value={record.support_phone} />
          <Field label={t("Support email")} value={record.support_email} link />
        </div>
      </div>

      <div>
        <SectionHeader label={t("Legal links")} />
        <div className="dr-grid">
          <Field label={t("Terms & Conditions URL")} value={record.terms_url} link />
          <Field label={t("Refund policy URL")} value={record.refund_policy_url} link />
          <Field full label={t("Cancellation policy URL")} value={record.cancellation_url} link />
        </div>
      </div>
    </Flex>
  )
}

const ProfileUpdateTab = ({ record, t, onReview }) => {
  const update = record.profile_update
  if (!update?.changes?.length) {
    return (
      <Flex vertical align="center" justify="center" gap={12} className="dr-empty">
        <Icon type="receipt_bill" style={{ fontSize: 32 }} />
        <Text type="secondary">{t("No pending update requests")}</Text>
      </Flex>
    )
  }
  return (
    <Flex vertical gap={16} className="dr-update">
      <Alert
        type="warning"
        showIcon
        message={`${t("Update pending")} - ${t("Submitted on")} ${utcDate(update.submitted_on)} - ${t("awaiting manual review.")}`}
      />
      <div className="dr-diff">
        <div className="dr-diff-head">
          <Text type="secondary">{t("Field")}</Text>
          <Text type="secondary">{t("Current")}</Text>
          <Text type="secondary">{t("Proposed")}</Text>
        </div>
        {update.changes.map((c) => (
          <div className="dr-diff-row" key={c.field}>
            <Text strong>{c.field}</Text>
            <Text type="secondary">{c.current}</Text>
            <Text>{c.proposed}</Text>
          </div>
        ))}
      </div>
      <Flex gap={12} justify="flex-end" className="dr-review-actions">
        <Button danger ghost onClick={() => onReview(record.id, "reject")}>
          {t("Reject")}
        </Button>
        <Button type="primary" onClick={() => onReview(record.id, "approve")}>
          {t("Approve")}
        </Button>
      </Flex>
    </Flex>
  )
}

const ActivityTab = ({ record, t }) => {
  const events = record.activity_log || []
  if (!events.length) {
    return (
      <Flex vertical align="center" justify="center" gap={12} className="dr-empty">
        <Text type="secondary">{t("No activity yet")}</Text>
      </Flex>
    )
  }
  return (
    <div className="dr-activity">
      <div className="dr-activity-head">
        <Text type="secondary">{t("Event")}</Text>
        <Text type="secondary">{t("Date and time")}</Text>
      </div>
      {events.map((e) => (
        <div className="dr-activity-row" key={e.id}>
          <Flex gap={8} align="center" wrap="wrap">
            <Text strong>[{e.actor}]</Text>
            {["pending", "enrolled", "unenrolled", "rejected", "needs_update"].includes(e.type) ? (
              <StatusTag status={e.type} />
            ) : (
              <Text>{e.event}</Text>
            )}
          </Flex>
          <Text type="secondary">{utcDate(e.timestamp)}</Text>
        </div>
      ))}
    </div>
  )
}

const RequestDetailsDrawer = ({ record, open, onClose, onReview }) => {
  const { t } = useTranslation()

  const handleDownload = () => {
    if (!record) return
    FileDownload(
      JSON.stringify(record, null, 2),
      `digital-receipt-${record.sequence_number || record.id}.json`
    )
  }

  const items = useMemo(() => {
    if (!record) return []
    return [
      {
        key: "overview",
        label: t("Overview"),
        children: <OverviewTab record={record} t={t} onDownloadLogo={handleDownload} />
      },
      {
        key: "profile_update",
        label: t("Profile update request"),
        children: <ProfileUpdateTab record={record} t={t} onReview={onReview} />
      },
      { key: "activity", label: t("Activity log"), children: <ActivityTab record={record} t={t} /> }
    ]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record])

  return (
    <Drawer
      title={
        <Flex align="center" justify="space-between" gap={12}>
          <Text className="dr-title">{t("Request details")}</Text>
          <Button
            className="flex center small-space"
            icon={<Icon type="file_download" />}
            onClick={handleDownload}
          >
            {t("Download")}
          </Button>
        </Flex>
      }
      getContainer={() => document.getElementById("main-layout")}
      width={600}
      placement="right"
      closable
      onClose={onClose}
      open={open}
      destroyOnClose
      closeIcon={<Icon type="cross_small" />}
      styles={{
        content: { borderRadius: "24px" },
        header: { padding: "16px 24px", borderBottom: "none" },
        body: { padding: "4px 24px 24px" }
      }}
      rootClassName="drawer-like-modal digital-receipt-drawer"
    >
      {record ? <Tabs items={items} className="dr-tabs" /> : null}
    </Drawer>
  )
}

export default RequestDetailsDrawer
